import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/modules/shared/api/axios";


  // fetch current user (used at app start)
export const fetchUser = createAsyncThunk(
  "auth/fetchUser",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/user", { withCredentials: true });
      // If the session is valid, OTP is no longer pending (guard against stale localStorage state).
      try {
        localStorage.removeItem("otp_pending");
      } catch (_) {
        // ignore storage errors
      }
      return res.data;
    } catch (e) {
      const status = e.response?.status;

      if (status === 401) {
        // No token or expired session
        return rejectWithValue({ message: "Unauthorized" });
      }

      if (status === 500) {
        console.error("Backend error:", e.response?.data);
        return rejectWithValue({ message: "Server error" });
      }

      return rejectWithValue(e.response?.data || { message: "Unknown error" });
    }
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async (payload, { rejectWithValue }) => {
    try {
      // Initiate login (send OTP) using unified_number + mobile
      const res = await api.post("/login", payload);
      // Expect { message, mobile }
      return res.data;
    } catch (e) {
      const errData = e.response?.data;
      return rejectWithValue({
        message: errData?.message || errData?.error || e.message || "Login failed",
      });
    }
  }
);

export const verifyLoginOtp = createAsyncThunk(
  "auth/verifyLoginOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await api.post("/login-otp/verify", payload);
      let userData = null;
      try {
        const me = await api.get("/user");
        userData = me.data;
      } catch (fetchErr) {
        userData = res.data?.user || null;
      }

      if (!userData) {
        throw new Error("OTP verification succeeded but user data missing");
      }

      // OTP completed successfully.
      try {
        localStorage.removeItem("otp_pending");
      } catch (_) {
        // ignore storage errors
      }

      return userData;
    } catch (e) {
      const errData = e.response?.data;
      return rejectWithValue(errData || { message: e.message || "OTP verification failed" });
    }
  }
);

export const register = createAsyncThunk(
  "auth/register",
  async (payload, { rejectWithValue }) => {
    try {
      // Support FormData (for file upload) and plain objects
      let data = payload;
      let config = undefined;
      if (typeof FormData !== "undefined" && payload instanceof FormData) {
        // Let axios set the correct multipart boundary
        config = { headers: { "Content-Type": "multipart/form-data" } };
      }
      const res = await api.post("/register", data, config);
      // Backend returns { status, message, data: { unified_number, mobile } }
      return res.data?.data || res.data;
    } catch (e) {
      const errData = e.response?.data;
      return rejectWithValue(
        errData || { message: e.message || "Registration failed" }
      );
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await api.post("/verify-otp", payload);
      return res.data.user;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const changeRegistrationMobile = createAsyncThunk(
  "auth/changeRegistrationMobile",
  async ({ unified_number, mobile }, { rejectWithValue }) => {
    try {
      const res = await api.post("/register/change-mobile", {
        unified_number,
        mobile,
      });
      return res.data;
    } catch (e) {
      const errData = e.response?.data;
      return rejectWithValue(
        errData || { error: e.message || "Failed to change mobile" }
      );
    }
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async ({ unified_number }, { rejectWithValue }) => {
    try {
      const res = await api.post("/auth/resend-otp", { unified_number });
      return res.data;
    } catch (e) {
      const errData = e.response?.data;
      return rejectWithValue(
        errData || { error: e.message || "Failed to resend OTP" }
      );
    }
  }
);


export const logout = createAsyncThunk("auth/logout", async () => {
  // Preserve remembered login while clearing other auth-related storage
  let rememberedLogin = null;
  try {
    rememberedLogin = localStorage.getItem("login_remember");
  } catch {
    rememberedLogin = null;
  }

  try {
    await api.post("/logout");
  } catch (e) {
    // ignore API errors, still proceed to clear client auth state
  }

  try {
    localStorage.clear();
    if (rememberedLogin) {
      localStorage.setItem("login_remember", rememberedLogin);
    }
  } catch {
    // ignore storage errors
  }

  window.location.href = "/login";
  return null;
});


const slice = createSlice({
  name: "auth",
  initialState: { user: null, status: "idle", error: null, loaded: false, },
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.loaded = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
        state.loaded = true;
      })
      .addCase(fetchUser.rejected, (state) => {
        state.user = null;
        state.loading = false;
        state.loaded = true;
      })

      // Login (OTP initiated; do not set user yet)
      .addCase(login.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.user = null;
        state.error = action.payload?.message || action.payload?.error || "Login failed";
      })

      // Verify Login OTP
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.user = action.payload;
        state.error = null;
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.error = action.payload?.message || action.payload?.error || "OTP verification failed";
      })
      // Verify general OTP (registration)
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.user = action.payload;
        state.error = null;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.error =
          action.payload?.message || action.payload?.error || "OTP verification failed";
      })
      .addCase(changeRegistrationMobile.rejected, (state, action) => {
        state.error =
          action.payload?.message || action.payload?.error || "Failed to change mobile";
      })

      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.error = null;
      })

      // Register (do not set user here; OTP verification/login occurs next)
      .addCase(register.fulfilled, (state, action) => {
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.user = null;
        state.error = action.payload?.message || "Registration failed";
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.error =
          action.payload?.message || action.payload?.error || "Failed to resend OTP";
      });
  },
});

export const { setUser } = slice.actions;
export default slice.reducer;
